import {Image} from '@volkswagen-onehub/components-core';
import React from 'react';
import Div from '../components/Div.styled';
import useMediaQuery from '../hooks/useMediaQuery';
import {useMeasure} from 'react-use';
import {
  ContainerStyled,
  TextContainer,
} from '../components/ContentWrapperStyled';
import DescriptionPage from '../components/HeadlineSubHeadlineText';
import TypographyBody from '../components/Typography/TypographyBody';
import TypographyHeading from '../components/Typography/TypographyHeading';
import {JSONMARKDOWN, MODELKMS, VWCARMODELLABELS} from '../types/results';
import {VWCarModel} from '../types/surveyResponse';
import {replaceText} from '../utils/stringUtils';
import {getContentWithHtml, hasHtmlTags} from '../utils/contentUtils';
import {ButtonCTAStyled} from '../components/CTAStyled';
import {CAR_MODEL} from '../constants/result';
import {getMoreAboutEvRangeLink} from '../utils/carUtils';

interface ResultSectionThreeProps {
  name: string;
  model: string;
  assetUrl: string;
  hero_sub_title: string;
  hero_title_line_1: string;
  hero_title_line_2: string;
  image1: string;
  image2: string;
  image3: string;
  headline: string;
  body: string;
}

const returncarModel = (model: VWCarModel | undefined) => {
  if (!model) return '';
  if (model !== 'id4Andid5' && model !== 'id4gtxAndid5gtx') return model;
  if (model === 'id4Andid5') return 'ID. 4 Pro & ID. 5 Pro';
  if (model === 'id4gtxAndid5gtx') return 'ID. 4 GTX & ID. 5 GTX';
  return model;
};

const returnModelKms = (model: VWCarModel) => {
  if (Object.keys(MODELKMS).includes(model))
    return MODELKMS[model as keyof typeof MODELKMS];
  return '';
};

const ResultSectionThree = ({
  name,
  model,
  assetUrl,
  headline,
  body,
  hero_sub_title,
  hero_title_line_1,
  hero_title_line_2,
  image1,
  image2,
  image3,
}: ResultSectionThreeProps) => {
  const {isDesktop} = useMediaQuery();
  const [imageTwoRef, {width: imageTwoWidth}] = useMeasure<HTMLDivElement>();
  const bodyReplacements = {
    [JSONMARKDOWN.name]: name,
    [JSONMARKDOWN.model]:
      VWCARMODELLABELS[
        returncarModel(model as VWCarModel | undefined) as VWCarModel
      ],
    [JSONMARKDOWN.model_km]: returnModelKms(model as VWCarModel) as string,
  };

  const bodyContentAfterReplacingMarksdowns = replaceText(
    body,
    bodyReplacements,
  );

  return (
    <Div margin={'250px 0 0'}>
      <DescriptionPage
        subTitle={hero_sub_title}
        title={hero_title_line_1}
        titleStrong={hero_title_line_2}
      />
      <ContainerStyled isDesktop={isDesktop}>
        <Div
          customStyles={{
            paddingInline: isDesktop ? '0' : '32px',
            flex: isDesktop ? '67%' : '100%',
            marginBottom: isDesktop ? 0 : 16,
          }}
        >
          <Image
            data-vw-variant={''}
            src={assetUrl + image1}
            alt="Range Img1"
            key={'range-1'}
            width={'100%'}
            height={'auto'}
            style={{maxWidth: !isDesktop ? '600px' : '100%'}}
          />
        </Div>
        <Div
          ref={imageTwoRef}
          customStyles={{
            paddingInline: isDesktop ? '0' : '32px',
            flex: isDesktop ? '33%' : '100%',
            marginBottom: isDesktop ? 0 : 16,
          }}
        >
          <Image
            data-vw-variant={''}
            src={assetUrl + image2}
            alt="Range Img2"
            key={'range-2'}
            width={'100%'}
            height={'auto'}
            style={{maxWidth: !isDesktop ? '600px' : '100%'}}
          />
        </Div>
      </ContainerStyled>

      <TextContainer
        isDesktop={isDesktop}
        style={{
          gap: isDesktop ? '133px' : '0',
          display: 'flex',
          justifyContent: isDesktop ? 'flex-end' : 'center',
        }}
      >
        <TypographyHeading
          color="black"
          size="xLarge"
          data-vw-variant={''}
          style={{
            paddingRight: isDesktop ? '32px' : 0,
            paddingInline: isDesktop ? '0' : '32px',
            maxWidth: '666px',
          }}
        >
          {headline}
        </TypographyHeading>

        <Div
          maxWidth={'600px'}
          customStyles={{
            paddingBottom: isDesktop ? 0 : '40px',
            paddingInline: isDesktop ? '0' : '32px',
            marginRight: isDesktop ? imageTwoWidth * 0.45 : undefined,
          }}
        >
          <Image
            data-vw-variant={''}
            src={assetUrl + image3}
            width={'100%'}
            height={'auto'}
            alt="Range Img3"
            key={'range-3'}
          />
        </Div>
      </TextContainer>

      <Div
        customStyles={{
          maxWidth: 666,
          margin: '0 auto',
          paddingBottom: 40,
        }}
      >
        <TypographyBody
          size="body"
          textAlign="left"
          color="black"
          style={{
            margin: isDesktop ? '80px 0 0' : '40px 0 0',
            paddingInline: isDesktop ? '0' : '32px',
          }}
        >
          {hasHtmlTags(bodyContentAfterReplacingMarksdowns)
            ? getContentWithHtml(bodyContentAfterReplacingMarksdowns)
            : bodyContentAfterReplacingMarksdowns}
        </TypographyBody>
        {model !== CAR_MODEL.Amarok && (
          <Div
            customStyles={{
              paddingInline: isDesktop ? '0' : '32px',
              paddingTop: isDesktop ? '80px' : '40px',
            }}
          >
            <ButtonCTAStyled
              tag="button"
              emphasis="secondary"
              onClick={() => {
                window.open(getMoreAboutEvRangeLink(model as VWCarModel));
              }}
              ariaLabel="aria label for CTA"
              size="large"
            >
              More about EV range
            </ButtonCTAStyled>
          </Div>
        )}
      </Div>
    </Div>
  );
};

export default ResultSectionThree;
